import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Lock, UserPlus, Eye, EyeOff, Sparkles } from 'lucide-react';
import { useAuth } from './AuthContext';
import { useNavigate, Link } from 'react-router-dom';

const getErrorMessage = (error: any): string => {
    console.error('Registrierungsfehler:', error);
    const code = error?.code;
    switch (code) {
        case 'auth/email-already-in-use':
            return 'Diese E-Mail-Adresse wird bereits verwendet.';
        case 'auth/invalid-email':
            return 'Bitte gib eine gültige E-Mail-Adresse ein.';
        case 'auth/weak-password':
            return 'Das Passwort muss mindestens 6 Zeichen lang sein.';
        case 'auth/network-request-failed':
            return 'Netzwerkfehler – bitte überprüfe deine Internetverbindung.';
        case 'auth/too-many-requests':
            return 'Zu viele Registrierungsversuche. Bitte warte einen Moment.';
        case 'auth/operation-not-allowed':
            return 'E-Mail/Passwort-Anmeldung ist deaktiviert. Bitte kontaktiere den Support.';
        default:
            return `Registrierung fehlgeschlagen: ${error.message || 'Unbekannter Fehler'}`;
    }
};

const Register = () => {
    const { currentUser, register } = useAuth();
    const isAnonymous = currentUser?.isAnonymous || false;

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [agreeTerms, setAgreeTerms] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const navigate = useNavigate();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!email.includes('@') || !email.includes('.')) {
            return setError('Bitte gib eine gültige E-Mail-Adresse ein.');
        }
        if (password.length < 8) {
            return setError('Das Passwort muss mindestens 8 Zeichen lang sein.');
        }
        if (password !== confirmPassword) {
            return setError('Die Passwörter stimmen nicht überein.');
        }
        if (!agreeTerms) {
            return setError('Bitte stimme den AGB und der Datenschutzerklärung zu.');
        }

        setLoading(true);
        try {
            await register(email, password);
            navigate('/verify-email', { state: { email } });
        } catch (err: any) {
            setError(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-indigo-50 flex items-center justify-center p-4 pt-24">
            <div className="absolute inset-0 -z-10 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob" />
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000" />
                <div className="absolute top-40 left-20 w-80 h-80 bg-pink-300 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000" />
            </div>

            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="w-full max-w-md"
            >
                <div className="bg-white/70 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/50 p-8 md:p-10">
                    <div className="text-center mb-8">
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                            className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-600 text-white shadow-lg mb-4"
                        >
                            <Sparkles className="w-8 h-8" />
                        </motion.div>
                        <h2 className="text-3xl font-bold text-gray-900 mb-2">
                            {isAnonymous ? 'Konto sichern' : 'Konto erstellen'}
                        </h2>
                        <p className="text-gray-600">
                            {isAnonymous
                                ? 'Verknüpfe dein anonymes Konto mit E-Mail und Passwort'
                                : 'Starte mit Studytino durch'}
                        </p>
                    </div>

                    {isAnonymous && (
                        <div className="bg-indigo-50 border border-indigo-200 text-indigo-700 p-3 rounded-xl mb-4 text-sm">
                            🔗 Du nutzt die App bereits ohne Konto. Deine vorhandenen Lernsets werden mit diesem neuen Konto verknüpft.
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-50 text-red-600 p-3 rounded-xl mb-4 text-sm">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                E-Mail-Adresse
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 bg-white/50 focus:bg-white focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100 transition-all outline-none"
                                    placeholder="deine@email.de"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Passwort
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    minLength={8}
                                    className="w-full pl-12 pr-12 py-3 rounded-xl border border-gray-200 bg-white/50 focus:bg-white focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100 transition-all outline-none"
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Mindestens 8 Zeichen</p>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Passwort bestätigen
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    className="w-full pl-12 pr-12 py-3 rounded-xl border border-gray-200 bg-white/50 focus:bg-white focus:border-indigo-300 focus:ring-4 focus:ring-indigo-100 transition-all outline-none"
                                    placeholder="••••••••"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>

                        <label className="flex items-start gap-3 text-sm text-gray-600 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={agreeTerms}
                                onChange={(e) => setAgreeTerms(e.target.checked)}
                                required
                                className="mt-1 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <span>
                                Ich stimme den <Link to="/agb" className="text-indigo-600 hover:underline">Allgemeinen Geschäftsbedingungen</Link> und der <Link to="/datenschutz" className="text-indigo-600 hover:underline">Datenschutzerklärung</Link> zu.
                            </span>
                        </label>

                        <motion.button
                            type="submit"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            disabled={loading}
                            className="w-full py-3.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                'Wird registriert...'
                            ) : (
                                <>
                                    <UserPlus className="w-5 h-5" />
                                    {isAnonymous ? 'Konto sichern & verknüpfen' : 'Registrieren'}
                                </>
                            )}
                        </motion.button>
                    </form>

                    <p className="text-center text-gray-600 mt-8">
                        Bereits registriert?{' '}
                        <Link to="/login" className="text-indigo-600 hover:text-indigo-800 font-semibold">
                            Zum Login
                        </Link>
                    </p>
                </div>
            </motion.div>
        </div>
    );
};

export default Register;